import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import MessageGroup from './MessageGroup';
import DateDivider from './DateDivider';
import UnreadDivider from './UnreadDivider';
import '../styles/MessageList.css';

function MessageList({
  messages,
  currentUser,
  onDelete,
  isLoading,
  typingUsers,
  onReply,
  firstUnreadMessageId = null,
  onLoadOlderMessages = null,
  onMarkAsRead = null,
  isAllMode = false,
  onNavigateToChannel = null
}) {
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const loadingTriggerRef = useRef(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [hasScrolledToUnread, setHasScrolledToUnread] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const prevScrollHeightRef = useRef(0);
  const prevFirstMessageIdRef = useRef(null);

  // Восстанавливать позицию скролла при добавлении старых сообщений (prepend)
  useLayoutEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const firstId = messages.length > 0 ? messages[0].id : null;
    if (prevFirstMessageIdRef.current && firstId !== prevFirstMessageIdRef.current) {
      // Был prepend — восстановить позицию
      container.scrollTop += container.scrollHeight - prevScrollHeightRef.current;
    }
    prevFirstMessageIdRef.current = firstId;
    prevScrollHeightRef.current = container.scrollHeight;
  }, [messages]);

    // Check if two dates are on different days
  const isDifferentDay = (date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    return d1.getFullYear() !== d2.getFullYear() ||
           d1.getMonth() !== d2.getMonth() ||
           d1.getDate() !== d2.getDate();
  };

  // Group messages by user and time
  const groupMessages = (messages) => {
    if (!messages || messages.length === 0) return [];

    const groups = [];
    let currentGroup = null;

    messages.forEach((message, index) => {
      const prevMessage = index > 0 ? messages[index - 1] : null;
      const timeDiff = prevMessage
        ? new Date(message.createdAt) - new Date(prevMessage.createdAt)
        : Infinity;

      // Start new group if:
      // - First message
      // - Different user
      // - Time gap > 5 minutes
      const shouldStartNewGroup =
        !currentGroup ||
        message.userId !== currentGroup.userId ||
        timeDiff > 300000;

      if (shouldStartNewGroup) {
        currentGroup = {
          userId: message.userId,
          username: message.username,
          displayName: message.displayName,
          avatar: message.avatar || null,
          isOwn: message.userId === currentUser.id,
          messages: [],
          firstMessageTime: message.createdAt
        };
        groups.push(currentGroup);
      }

      currentGroup.messages.push(message);
    });

    return groups;
  };

  const scrollToBottom = (behavior = 'smooth') => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const doScroll = () => {
      container.scrollTop = container.scrollHeight;
    };

    doScroll();
    setTimeout(doScroll, 50);
    setTimeout(doScroll, 100);
    setTimeout(doScroll, 200);
    setTimeout(doScroll, 400);
  };

  const scrollToMessage = (messageId) => {
    const messageElement = document.getElementById(`message-${messageId}`);
    if (messageElement && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const messageTop = messageElement.offsetTop;
      const containerHeight = container.clientHeight;
      const scrollTo = messageTop - containerHeight / 3;

      container.scrollTo({
        top: Math.max(0, scrollTo),
        behavior: 'smooth'
      });

      // Подсветка сообщения
      messageElement.classList.add('highlight-message');
      setTimeout(() => {
        messageElement.classList.remove('highlight-message');
      }, 2000);
    }
  };

  const checkIfNearBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return true;

    const threshold = 150;
    const scrollHeight = container.scrollHeight;
    const scrollTop = container.scrollTop;
    const clientHeight = container.clientHeight;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const nearBottom = distanceFromBottom <= threshold;

    setIsNearBottom(nearBottom);
    setShowScrollButton(!nearBottom && messages.length > 5);

    // Если пользователь прокрутил вниз, отметить сообщения как прочитанные
    if (nearBottom && messages.length > 0 && onMarkAsRead) {
      const lastMessage = messages[messages.length - 1];
      onMarkAsRead(lastMessage.id);
    }

    return nearBottom;
  };

  // Intersection Observer для загрузки старых сообщений
  useEffect(() => {
    // Не активировать пока не завершена начальная загрузка и прокрутка
    if (!initialLoadDone || !onLoadOlderMessages || isLoadingOlder || messages.length === 0 || !hasMoreMessages) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && !isLoadingOlder && messages.length > 0 && hasMoreMessages && initialLoadDone) {
          const oldestMessage = messages[0];
          setIsLoadingOlder(true);
          onLoadOlderMessages(oldestMessage.id).then((hasMore) => {
            setTimeout(() => {
              setIsLoadingOlder(false);
              setHasMoreMessages(hasMore !== false); // Если вернул false, значит больше нет
            }, 1000); // Debounce для предотвращения повторных запросов
          }).catch(() => {
            setIsLoadingOlder(false);
            setHasMoreMessages(false); // При ошибке прекращаем загрузку
          });
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const currentTrigger = loadingTriggerRef.current;
    if (currentTrigger) {
      observer.observe(currentTrigger);
    }

    return () => {
      if (currentTrigger) {
        observer.unobserve(currentTrigger);
      }
      observer.disconnect();
    };
  }, [messages.length, onLoadOlderMessages, isLoadingOlder, hasMoreMessages, initialLoadDone]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', checkIfNearBottom);
    return () => container.removeEventListener('scroll', checkIfNearBottom);
  }, [messages.length]);

  useEffect(() => {
    if (isNearBottom && messages.length > 0) {
      setTimeout(() => {
        scrollToBottom('auto');
      }, 50);
    }
  }, [messages]);

  // Сбросить hasMoreMessages при загрузке новых сообщений (смена канала)
  useEffect(() => {
    setHasMoreMessages(true);
    setInitialLoadDone(false);
    setHasScrolledToUnread(false);
  }, [messages.length === 0 ? 'empty' : messages[0]?.channelId]);

  // Scroll to first unread message on initial load
  useEffect(() => {
    if (!initialLoadDone && messages.length > 0) {
      setTimeout(() => {
        if (firstUnreadMessageId && !hasScrolledToUnread) {
          scrollToMessage(firstUnreadMessageId);
          setHasScrolledToUnread(true);
        } else {
          scrollToBottom('auto');
        }
        setInitialLoadDone(true);
        checkIfNearBottom();
      }, 200);
    }
  }, [messages.length, initialLoadDone, firstUnreadMessageId, hasScrolledToUnread]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      if (isNearBottom) {
        container.scrollTop = container.scrollHeight;
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isNearBottom]);

  const handleScrollButtonClick = () => {
    scrollToBottom('smooth');
    setTimeout(() => {
      setIsNearBottom(true);
      setShowScrollButton(false);
    }, 300);
  };

  const handleImageLoad = () => {
    if (isNearBottom) {
      setTimeout(() => {
        scrollToBottom('auto');
      }, 50);
    }
  };

  if (isLoading) {
    return (
      <div className="message-list">
        <div className="loading-messages">
          <div className="spinner"></div>
          <p>Загрузка сообщений...</p>
        </div>
      </div>
    );
  }

  const messageGroups = groupMessages(messages);

  return (
    <div className="message-list" ref={messagesContainerRef}>
      {messages.length === 0 ? (
        <div className="empty-messages">
          <div className="empty-icon">💬</div>
          <p>Пока нет сообщений</p>
          <span>Начните общение!</span>
        </div>
      ) : (
        <div className="messages">
          {/* Триггер для загрузки старых сообщений */}
          <div ref={loadingTriggerRef} style={{ height: '1px' }}>
            {isLoadingOlder && (
              <div className="loading-older-messages">
                <div className="spinner-small"></div>
                <span>Загрузка...</span>
              </div>
            )}
          </div>

          {messageGroups.map((group, groupIndex) => {
            const prevGroup = groupIndex > 0 ? messageGroups[groupIndex - 1] : null;
            const showDateDivider = !prevGroup ||
              isDifferentDay(group.firstMessageTime, prevGroup.firstMessageTime);

            return (
              <React.Fragment key={`${group.userId}-${group.firstMessageTime}-${groupIndex}`}>
                {showDateDivider && (
                  <DateDivider date={group.firstMessageTime} />
                )}

                {/* Проверяем, нужно ли показать разделитель непрочитанных перед этой группой */}
                {group.messages.some(msg => msg.id === firstUnreadMessageId) && (
                  <UnreadDivider />
                )}

                <MessageGroup
                  group={group}
                  currentUser={currentUser}
                  onImageLoad={handleImageLoad}
                  onReply={onReply}
                  onDelete={onDelete}
                  firstUnreadMessageId={firstUnreadMessageId}
                  isAllMode={isAllMode}
                  onNavigateToChannel={onNavigateToChannel}
                />
              </React.Fragment>
            );
          })}

          {typingUsers.size > 0 && (
            <div className="typing-indicator">
              <div className="typing-avatar">
                {Array.from(typingUsers)[0][0].toUpperCase()}
              </div>
              <div className="typing-bubble">
                <span className="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>
      )}

      {showScrollButton && (
        <button className="scroll-to-bottom" onClick={handleScrollButtonClick}>
          <span className="scroll-icon">↓</span>
        </button>
      )}
    </div>
  );
}

export default MessageList;
